import { mergeChangeRequestIntoTicket } from "@sd/lib/changeRequestTicketFields";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function looksLikeTicketRecord(value: Record<string, unknown> | null): boolean {
  if (!value) return false;
  return Boolean(
    asRecord(value.ticket) ||
      asRecord(value.change_request) ||
      asRecord(value.pts_task) ||
      value.subject ||
      value.id ||
      value.ui_id
  );
}

/** Flatten GET `/service_desk/ticket_create/{id}/` for form pre-fill (includes PTS + CR). */
export function normalizeTicketCreateDetailPayload(
  raw: unknown
): Record<string, unknown> {
  if (!raw || typeof raw !== "object") {
    return {};
  }

  const original = raw as Record<string, unknown>;
  const nestedData = asRecord(original.data);
  const originalHasEnvelope =
    Boolean(asRecord(original.ticket)) ||
    Boolean(asRecord(original.pts_task)) ||
    Boolean(asRecord(original.change_request)) ||
    typeof original.change_request === "string";
  const root = originalHasEnvelope
    ? original
    : looksLikeTicketRecord(nestedData)
      ? nestedData!
      : original;

  const ticket = asRecord(root.ticket) ?? root;

  const merged: Record<string, unknown> = { ...ticket };

  const ptsTask =
    asRecord(root.pts_task) ??
    asRecord(original.pts_task) ??
    asRecord(ticket.pts_task);

  if (ptsTask) {
    merged.is_pts = merged.is_pts ?? true;
    for (const key of [
      "project",
      "parent_task",
      "taskestimatedhours",
      "work_type",
      "pts_status",
    ] as const) {
      if (
        (merged[key] === undefined ||
          merged[key] === null ||
          merged[key] === "") &&
        ptsTask[key] !== undefined &&
        ptsTask[key] !== null &&
        ptsTask[key] !== ""
      ) {
        merged[key] = ptsTask[key];
      }
    }
    if (!merged.pts_task_id && ptsTask.id) {
      merged.pts_task_id = ptsTask.id;
    }
  }

  mergeChangeRequestIntoTicket(merged, root);
  mergeChangeRequestIntoTicket(merged, original);
  const nestedChangeRequest =
    asRecord(root.change_request) ??
    asRecord(original.change_request) ??
    asRecord(ticket.change_request);
  if (nestedChangeRequest) {
    merged.change_request = nestedChangeRequest;
  }

  if (
    merged.assigned &&
    (merged.assigned_to === undefined ||
      merged.assigned_to === null ||
      merged.assigned_to === "")
  ) {
    merged.assigned_to = merged.assigned;
  }

  for (const key of [
    "activity_logs",
    "activity",
    "requester_name",
    "assigned_name",
    "assignee_name",
    "created_by_name",
    "priority_label",
    "priority_id",
    "is_vip",
    "is_sla",
    "is_change_request",
    "requested_date",
    "expected_completion_date",
    "assign_employees",
  ] as const) {
    if (isEmptyValue(merged[key]) && !isEmptyValue(root[key])) {
      merged[key] = root[key];
    }
    if (isEmptyValue(merged[key]) && !isEmptyValue(original[key])) {
      merged[key] = original[key];
    }
    if (isEmptyValue(merged[key]) && !isEmptyValue(ticket[key])) {
      merged[key] = ticket[key];
    }
  }

  if (
    (merged.attachments === undefined ||
      merged.attachments === null ||
      merged.attachments === "" ||
      merged.attachments === "[]") &&
    root.attachments !== undefined &&
    root.attachments !== null
  ) {
    merged.attachments = root.attachments;
  }

  for (const key of ["file_path", "file_paths"] as const) {
    if (
      (merged[key] === undefined ||
        merged[key] === null ||
        merged[key] === "" ||
        (Array.isArray(merged[key]) &&
          (merged[key] as unknown[]).length === 0)) &&
      root[key] !== undefined &&
      root[key] !== null
    ) {
      merged[key] = root[key];
    }
  }

  return merged;
}
